/**
 * Answers the one question blocking the geofence vendor decision, without
 * needing the vendor.
 *
 * `threatmetrix.Geofence.softCheck()` accepts no coordinates. It acquires its
 * own fix, which means it calls `navigator.geolocation` inside whatever browser
 * it is running in. Our Capacitor WebView is the case in doubt: Lobby.tsx
 * deliberately routes around `navigator.geolocation` there and uses the native
 * @capacitor/geolocation plugin instead, on the grounds that the browser API
 * "just times out".
 *
 * If that is still true, the vendor cannot get a fix on iOS or Android and we
 * have no fallback, because we cannot hand the SDK a position we obtained some
 * other way. If it is no longer true (Capacitor's BridgeWebChromeClient does
 * request the runtime permission, and WKWebView has supported geolocation since
 * iOS 15), the mobile half of the integration is unblocked.
 *
 * That is a plain browser-API question. It needs no API key, no LexisNexis
 * contract, and no vendor script: run this on a real device and read the
 * verdict.
 *
 * HOW TO RUN
 *   1. Build and install a debug build on a real device (not the simulator:
 *      simulated location bypasses exactly the plumbing under test).
 *   2. Attach over CDP. See .claude/skills/android-webview-perf for the
 *      adb + Chrome DevTools Protocol setup already used for profiling.
 *   3. In the console:  await window.__geoProbe()
 *
 * Both paths are run because the comparison is what makes the result
 * conclusive. `navigator` failing on its own could mean permission was denied
 * or GPS was cold. `navigator` failing while `capacitor` succeeds, on the same
 * device, seconds apart, means the WebView bridge is the problem.
 */
import { Capacitor } from '@capacitor/core';

import { GEOLOCATION_OPTIONS } from './locationCheck';

export type ProbeLeg = {
  /** `navigator` is the path the vendor SDK will use; `capacitor` is our control. */
  path: 'navigator' | 'capacitor';
  ok: boolean;
  ms: number;
  errorCode?: number;
  errorMessage?: string;
  accuracyMetres?: number;
};

export type ProbeResult = {
  platform: string;
  permission: string;
  legs: ProbeLeg[];
  /** Plain-language answer to "can the vendor get a fix here". */
  verdict: string;
};

/** Geolocation API error codes, named so a report does not read as bare integers. */
const ERROR_NAMES: Record<number, string> = {
  1: 'PERMISSION_DENIED',
  2: 'POSITION_UNAVAILABLE',
  3: 'TIMEOUT'
};

function describeError(err: {
  code?: number;
  message?: string;
}): Pick<ProbeLeg, 'errorCode' | 'errorMessage'> {
  const code = err?.code;
  const named = code !== undefined ? ERROR_NAMES[code] : undefined;
  return {
    errorCode: code,
    errorMessage: named ? `${named}${err?.message ? `: ${err.message}` : ''}` : err?.message
  };
}

/** The path the vendor SDK will take. This is the leg that actually matters. */
function probeNavigator(): Promise<ProbeLeg> {
  const started = Date.now();
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({
        path: 'navigator',
        ok: false,
        ms: 0,
        errorMessage: 'navigator.geolocation is undefined in this WebView'
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          path: 'navigator',
          ok: true,
          ms: Date.now() - started,
          accuracyMetres: position.coords.accuracy
        }),
      (err) =>
        resolve({ path: 'navigator', ok: false, ms: Date.now() - started, ...describeError(err) }),
      GEOLOCATION_OPTIONS
    );
  });
}

/** Control leg: the native plugin we use in production today. */
async function probeCapacitor(): Promise<ProbeLeg> {
  const started = Date.now();
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    const position = await Geolocation.getCurrentPosition(GEOLOCATION_OPTIONS);
    return {
      path: 'capacitor',
      ok: true,
      ms: Date.now() - started,
      accuracyMetres: position.coords.accuracy
    };
  } catch (err: any) {
    return { path: 'capacitor', ok: false, ms: Date.now() - started, ...describeError(err || {}) };
  }
}

async function readPermission(): Promise<string> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Geolocation } = await import('@capacitor/geolocation');
      const status = await Geolocation.checkPermissions();
      return `location=${status.location} coarse=${status.coarseLocation}`;
    }
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    return status?.state ?? 'unknown';
  } catch {
    return 'unreadable';
  }
}

/**
 * Read the two legs together. Only the combination is conclusive, so the
 * verdicts below are written as the actual answer to give the team rather than
 * as raw data for someone else to interpret.
 */
function readVerdict(navigatorLeg: ProbeLeg, capacitorLeg: ProbeLeg, isNative: boolean): string {
  if (navigatorLeg.ok) {
    return isNative
      ? `WORKS. navigator.geolocation returned a fix in ${navigatorLeg.ms}ms inside the WebView, so the vendor SDK can acquire a position on this platform.`
      : `WORKS. navigator.geolocation returned a fix in ${navigatorLeg.ms}ms in the browser.`;
  }

  if (navigatorLeg.errorCode === 1) {
    return 'INCONCLUSIVE. Location permission was denied, so this run says nothing about the WebView bridge. Grant permission and run again.';
  }

  if (capacitorLeg.ok) {
    return `BLOCKED. The native plugin got a fix in ${capacitorLeg.ms}ms but navigator.geolocation failed (${navigatorLeg.errorMessage}). The vendor SDK cannot get a position here, and we cannot hand it one, because softCheck() takes no coordinates.`;
  }

  return `INCONCLUSIVE. Both paths failed (navigator: ${navigatorLeg.errorMessage}; plugin: ${capacitorLeg.errorMessage}). Likely no GPS fix available at all rather than a WebView problem. Retry outdoors or near a window.`;
}

export async function probeWebViewGeolocation(): Promise<ProbeResult> {
  const isNative = Capacitor.isNativePlatform();
  const permission = await readPermission();

  // Sequential, not parallel. Two simultaneous location requests can be
  // coalesced or can contend for the same GPS session, which would make a
  // failure ambiguous at exactly the moment we need it to be clear.
  const navigatorLeg = await probeNavigator();
  const capacitorLeg = isNative
    ? await probeCapacitor()
    : { path: 'capacitor' as const, ok: false, ms: 0, errorMessage: 'not a native platform' };

  return {
    platform: isNative ? Capacitor.getPlatform() : 'web',
    permission,
    legs: [navigatorLeg, capacitorLeg],
    verdict: readVerdict(navigatorLeg, capacitorLeg, isNative)
  };
}

/**
 * Expose the probe for manual invocation over CDP.
 *
 * Never installed on production or live. It triggers a location prompt and
 * reports the device position to the console, which is not something a shipped
 * app should carry an unguarded handle to.
 *
 * GUARDED ON STAGE, NOT NODE_ENV, for the same reason as mock mode in
 * utils/geofenceVerify: `react-scripts build` sets `NODE_ENV=production` for
 * every build it produces, lower environments included. Guarding on it did not
 * mean "not the live site", it meant "not a dev server", and the minifier then
 * dead-code-eliminated the whole function. Verified against the develop3
 * bundle: `__geoProbe` appeared zero times, so the probe could never have been
 * run on the deployed build it was written for.
 */
const LIVE_STAGES = ['production', 'live'];

export function installGeolocationProbe(): void {
  const stage = (process.env.REACT_APP_ENVIRONMENT ?? '').toLowerCase();
  if (LIVE_STAGES.includes(stage)) return;
  (window as any).__geoProbe = async () => {
    const result = await probeWebViewGeolocation();
    console.log(`[geoProbe] ${result.verdict}`);
    console.table(result.legs);
    return result;
  };
  console.log('[geoProbe] ready - run `await window.__geoProbe()` to test WebView geolocation');
}
